import { useCallback, useEffect, useMemo, useState } from 'react';
import useSWR from 'swr';
import {
 Heading,
 Flex,
 Box,
 Button,
 Progress,
 Text,
 useToast,
 Center,
} from '@chakra-ui/react';
import { fetchSurveyData } from '../../services/fetch';
import baseService from '../../services/base';
import { useSSO } from '../../context/SSO';
import { AttachEvidenceModal, EditStatusModal } from '../../components/Modals';
import { QuestionCardSimple } from '../../components/Card';
import { DownloadResponsesButton } from '../../components/Button';
import { Select } from '../../components/Input';
import QuestionCardSCertificationType from '../../components/Card/Question-card-certification-quest';
import StatusTag from '../../components/Tags/StatusTag';

class APIError extends Error {
 constructor(message, status) {
  super(message);
  this.name = 'APIError';
  this.status = status;
 }
}

async function fetchData(url) {
 try {
  const response = await baseService.get(url);
  if (!response?.data || Object.keys(response?.data)?.length === 0) {
   throw new APIError('No data available', 404);
  }
  return response.data;
 } catch (error) {
  if (error.response) {
   throw new APIError(error.response.data.message || 'API Error', error.response.status);
  }
  throw new APIError('Network error', 500);
 }
}

export default function CertificationType({
 companyId,
 surveyId,
 companyGroupId,
 formName,
 selectedCompanyId,
 isResponsibleUser = false,
 hasPillars,
}) {
 const toast = useToast();
 const { user } = useSSO();

 const [isEvidenceModalOpen, setEvidenceModalOpen] = useState(false);
 const [currentQuestionId, setCurrentQuestionId] = useState();
 const [currentFiles, setCurrentFiles] = useState([]);
 const [selectedPillar, setSelectedPillar] = useState();
 const [isLoadingEvidence, setLoadingEvidence] = useState(false);
 const [isLoadingResponses, setLoadingResponses] = useState(false);
 const [certificationControlId, setCertificationControlId] = useState();
 const [isEditStatusModalOpen, setIsEditStatusModalOpen] = useState(false);

 const fetchFunction = hasPillars ? fetchData : fetchSurveyData;
 const endpoint = hasPillars
  ? `survey/diagnostic/${surveyId}?id_company=${companyId}`
  : `survey/respondent/company?surveyId=${surveyId}&id_company=${selectedCompanyId}`;

 const { data } = useSWR(surveyId && selectedCompanyId ? endpoint : null, fetchFunction);

 const { data: currentPillarData } = useSWR(
  selectedPillar
   ? `survey/admin/pillar/answered/certificate?id_company=${companyId}&id_pillar=${selectedPillar}&id_survey=${surveyId}`
   : null,
  fetchData
 );

 const { data: statusData, mutate } = useSWR(
  certificationControlId ? `certificate-status-control/${certificationControlId}` : null,
  fetchData
 );

 async function fetchCertificationControlId(surveyId) {
  try {
   const response = await baseService.get(
    `certificate/link-certificate/certificate-status-control/${surveyId}`,
    {
     headers: {
      Accept: 'text/html,application/json',
     },
    }
   );

   return parseInt(response.data, 10);
  } catch (error) {
   console.error('Error fetching certification control ID:', error);
   throw error;
  }
 }

 useEffect(() => {
  if (hasPillars && data?.pillars?.length) {
   setSelectedPillar(data?.pillars[0]?.id_pillar);
  }

  if (surveyId && !certificationControlId) {
   fetchCertificationControlId(surveyId)
    .then((id) => {
     if (!isNaN(id)) {
      setCertificationControlId(id);
     }
    //   else {
    //   throw new Error('Invalid certification control ID received');
    //  }
    })
    .catch((error) => {
     console.error('Error fetching certification control ID:', error);
    });
  }
 }, [data, hasPillars, surveyId, certificationControlId]);

 const pillarOptions = useMemo(() => {
  if (!hasPillars || !data?.pillars?.length) return [];
  return data?.pillars?.map((pillar) => ({
   label: pillar.name_pillar,
   value: pillar.id_pillar,
  }));
 }, [data, hasPillars]);

 const currentQuestions = useMemo(() => {
  if (!data) return [];
  if (hasPillars && currentPillarData?.pillar?.questions?.length) {
   return currentPillarData?.pillar?.questions || [];
  }
  return data?.survey?.questions || [];
 }, [data, hasPillars, currentPillarData]);

 const totalQuestions = currentQuestions?.length || 0;

 const answeredQuestions = useMemo(() => {
  return currentQuestions.filter((question) => {
   const hasResponse =
    question.user_responses ||
    question.users_responses?.some(
     (response) => response?.company?.id_company === +selectedCompanyId
    );

   if (!hasResponse) return false;

   // Se a questão tem revisor vinculado, verificar aprovação
   if (question.has_reviewer) {
    // Para user_responses (resposta única)
    if (question.user_responses) {
     return question.user_responses.reviewer_approved === true;
    }
    
    // Para users_responses (múltiplas respostas)
    if (Array.isArray(question.users_responses)) {
     return question.users_responses.some(
      (response) => 
       response?.company?.id_company === +selectedCompanyId &&
       response.reviewer_approved === true
     );
    }
    
    return false;
   }

   // Se não tem revisor, apenas verificar se tem resposta
   return hasResponse;
  }).length;
 }, [currentQuestions, selectedCompanyId]);

 const hasEvidenceFiles = useMemo(() => {
  return currentQuestions.some((question) =>
   question.users_responses?.some(
    (response) =>
     response?.company?.id_company === +selectedCompanyId &&
     response?.response_file?.length > 0
   )
  );
 }, [currentQuestions, selectedCompanyId]);

 const handleQuestionEvidence = useCallback((questionId, response) => {
  setCurrentQuestionId(questionId);
  setCurrentFiles(response?.response_file);
  setEvidenceModalOpen(true);
 }, []);

 const handleCloseCurrentEvidence = () => {
  setEvidenceModalOpen(false);
  setCurrentQuestionId(null);
  setCurrentFiles([]);
 };

 async function evidenceDownload() {
  setLoadingEvidence(true);
  try {
   const response = await baseService.get(
    `/downloads?survey=${surveyId}&group=${companyGroupId}`,
    {
     responseType: 'blob',
     timeout: 30000,
    }
   );

   if (!response?.data) {
    throw new Error('No data received from server');
   }

   const blob = new Blob([response.data], { type: 'application/zip' });
   const url = window.URL.createObjectURL(blob);
   const link = document.createElement('a');
   link.href = url;
   link.download = `${formName}_evidence.zip`;

   document.body.appendChild(link);
   link.click();
   document.body.removeChild(link);
   window.URL.revokeObjectURL(url);

   toast({
    title: 'Download das evidências iniciado com sucesso',
    status: 'success',
   });
  } catch (error) {
   console.error('Evidence download error:', error);
   let errorMessage = 'Não foi possível processar os arquivos anexados.';

   if (error?.response?.status === 404) {
    errorMessage = 'Arquivos não encontrados.';
   } else if (error?.response?.status === 413) {
    errorMessage = 'Arquivos muito grandes para download.';
   } else if (error?.code === 'ECONNABORTED') {
    errorMessage = 'Tempo limite excedido. Tente novamente.';
   }

   toast({
    title: 'Erro ao fazer o download das evidências',
    description: errorMessage,
    status: 'error',
   });
  } finally {
   setLoadingEvidence(false);
  }
 }

 const HeaderSection = () => (
  <>
   {!isResponsibleUser && (
    <Flex gap='5' overflowY='hidden' pos='absolute' right='32px' top='70px'>
     <DownloadResponsesButton
      surveyId={surveyId}
      companyId={selectedCompanyId}
      companyGroupId={companyGroupId}
      formName={formName}
      isLoadingResponses={isLoadingResponses}
      setLoadingResponses={setLoadingResponses}
      height='51px'
     />
     <Button
      variant='secondary'
      width='260px'
      height='51px'
      isLoading={isLoadingEvidence}
      isDisabled={!hasEvidenceFiles}
      onClick={evidenceDownload}
     >
      Download das evidências
     </Button>
    </Flex>
   )}

   <Flex h='51px' align='center' justify='space-between' gap='6'>
    <Heading as='h3' fontSize='md'>
     Questões
    </Heading>

    {!hasPillars && (
     <Flex align='center' gap='6'>
      <Flex
       h='51px'
       align='center'
       gap='14px'
       px='20px'
       bg='rgba(0, 115, 187, 0.1)'
       borderRadius='12px'
      >
       <Text as='span' fontSize='sm'>
        Respondidas
       </Text>

       <Box height='10px' w='114px'>
        <Progress
         value={(answeredQuestions / totalQuestions) * 100}
         height='10px'
         borderRadius='21px'
         bg='#E5E5E5'
        />
       </Box>
       <Text
        as='span'
        fontSize='sm'
        bg='white'
        fontWeight={600}
        p='8px 10px'
        h='35px'
        borderRadius='4px'
       >
        {answeredQuestions < 10 && '0'}
        {answeredQuestions || 0}/{totalQuestions < 10 && '0'}
        {totalQuestions || 0}
       </Text>
      </Flex>

      <Flex
       h='51px'
       align='center'
       gap='14px'
       px='20px'
       bg='rgba(234, 88, 43, 0.1)'
       borderRadius='12px'
      >
       <Text as='span' fontSize='sm'>
        Obrigatórias
       </Text>

       <Box height='10px' w='114px'>
        <Progress
         value={(answeredQuestions / totalQuestions) * 100}
         height='10px'
         borderRadius='21px'
         bg='#E5E5E5'
         colorScheme='orange'
        />
       </Box>
       <Text
        as='span'
        fontSize='sm'
        bg='white'
        fontWeight={600}
        p='8px 10px'
        h='35px'
        borderRadius='4px'
       >
        {answeredQuestions < 10 && '0'}
        {answeredQuestions || 0}/ {totalQuestions < 10 && '0'}
        {totalQuestions || 0}
       </Text>
      </Flex>
     </Flex>
    )}

    {hasPillars && selectedPillar && (
     <Flex flex='3' align='center' justifyContent='flex-end' gap='20px'>
      <Flex minW='210px' w='21.96%' maxW='271px'>
       <Select
        name='currentPillar'
        options={pillarOptions}
        value={selectedPillar}
        defaultValue={selectedPillar}
        onChange={(e) => setSelectedPillar(+e.target.value)}
        h='49px'
       />
      </Flex>
      <Flex w='30.07%' overflowX='hidden' minW='27.07%' maxW='371px'>
       <Center
        bg='rgba(89, 143, 60, 0.1)'
        height='50px'
        borderRadius='12px'
        gap='16px'
        px='16px'
        w='full'
        maxW='370px'
       >
        <Text fontSize='sm' whiteSpace='nowrap'>
         Questões concluídas:
        </Text>

        <Box w='114px'>
         <Progress
          value={(answeredQuestions / totalQuestions) * 100}
          height='10px'
          borderRadius='21px'
          colorScheme='green'
          bg='#D4D4D4'
         />
        </Box>

        <Center px='6px' h='32px' bg='white'>
         <Text color='#598F3C' fontSize='sm' fontWeight='bold'>
          {answeredQuestions < 10 && '0'}
          {answeredQuestions}/{totalQuestions < 10 && '0'}
          {totalQuestions}
         </Text>
        </Center>
       </Center>
      </Flex>
     </Flex>
    )}
   </Flex>
  </>
 );

 if (!data || !currentQuestions?.length) {
  return null;
 }

 return (
  <Box>
   <Box bg='white' p='16px' borderRadius='12px'>
    <Flex h='63px' px='16px' bg='#F9F9F9' align='center' gap='12px' mb='16px'>
     <Flex gap='12px' h='31px' align='center' ml='auto'>
      <Text fontWeight={700}>Status:</Text>
      <StatusTag status={statusData?.status || 'não enviado'} />
     </Flex>

     {!isResponsibleUser &&
      statusData?.status &&
      statusData?.status !== 'não enviado' && (
       <Button
        variant='underline'
        fontWeight={600}
        fontSize={'15px'}
        onClick={() => setIsEditStatusModalOpen(true)}
       >
        Editar status
       </Button>
      )}
    </Flex>
    <HeaderSection />
   </Box>

   <Box mt='6'>
    {currentQuestions?.map((question, index) =>
     hasPillars ? (
      <QuestionCardSCertificationType
       key={`${question?.id_question}_${index}`}
       question={question}
       selectedCompanyId={selectedCompanyId}
       handleQuestionEvidence={handleQuestionEvidence}
       surveyId={surveyId}
       userId={user?.user?.id_user}
       questionnaireType='certification'
      />
     ) : (
      <QuestionCardSimple
       key={`${question?.id_question}_${index}`}
       question={question}
       selectedCompanyId={selectedCompanyId}
       handleQuestionEvidence={handleQuestionEvidence}
       surveyId={surveyId}
       userId={user?.user?.id_user}
       questionnaireType='certification'
      />
     )
    )}
   </Box>

   <EditStatusModal
    isOpen={isEditStatusModalOpen}
    onClose={() => setIsEditStatusModalOpen(false)}
    currentStatus={statusData?.status}
    certificateId={statusData?.id}
    onStatusUpdate={() => mutate()}
    certificateStatusControlId={statusData?.id}
   />

   {currentQuestionId && (
    <AttachEvidenceModal
     isOpen={isEvidenceModalOpen}
     onClose={handleCloseCurrentEvidence}
     questionId={currentQuestionId}
     files={currentFiles}
    />
   )}
  </Box>
 );
}
